/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationTriggerMetadata} from '@angular/animations';
import {ɵAnimationEngine as AnimationEngine} from '@angular/animations/browser';
import {Injectable, NgZone, Renderer2, RendererFactory2, RendererStyleFlags2, RendererType2} from '@angular/core';
export class AnimationRendererFactory implements RendererFactory2 {
private _currentId: number = 0;
private _microtaskId: number = 1;
private _animationCallbacksBuffer: [(e: any) => any, any][] = [];
private _rendererCache = new Map<Renderer2, BaseAnimationRenderer>();
/**
 * @param {?} delegate
 * @param {?} engine
 * @param {?} _zone
 */
constructor(
private delegate: RendererFactory2,
private engine: AnimationEngine,
private _zone: NgZone) {
    engine.onRemovalComplete = (element: any, delegate: Renderer2) => {
      // Note: if an component element has a leave animation, and the component
      // a host leave animation, the view engine will call `removeChild` for the parent
      // component renderer as well as for the child component renderer.
      // Therefore, we need to check if we already removed the element.
      if (delegate && delegate.parentNode(element)) {
        delegate.removeChild(element.parentNode, element);
      }
    };
  }
/**
 * @param {?} hostElement
 * @param {?} type
 * @return {?}
 */
createRenderer(hostElement: any, type: RendererType2): Renderer2 {
    const /** @type {?} */ EMPTY_NAMESPACE_ID = '';

    // cache the delegates to find out which cached delegate can
    // be used by which cached renderer
    const /** @type {?} */ delegate = this.delegate.createRenderer(hostElement, type);
    if (!hostElement || !type || !type.data || !type.data['animation']) {
      let /** @type {?} */ renderer: BaseAnimationRenderer|undefined = this._rendererCache.get(delegate);
      if (!renderer) {
        renderer = new BaseAnimationRenderer(EMPTY_NAMESPACE_ID, delegate, this.engine);
        // only cache this result when the base renderer is used
        this._rendererCache.set(delegate, renderer);
      }
      return renderer;
    }

    const /** @type {?} */ componentId = type.id;
    const /** @type {?} */ namespaceId = type.id + '-' + this._currentId;
    this._currentId++;

    this.engine.register(namespaceId, hostElement);
    const /** @type {?} */ animationTriggers = /** @type {?} */(( type.data['animation'] as AnimationTriggerMetadata[]));
    animationTriggers.forEach(
        trigger => this.engine.registerTrigger(
            componentId, namespaceId, hostElement, trigger.name, trigger));
    return new AnimationRenderer(this, namespaceId, delegate, this.engine);
  }
/**
 * @return {?}
 */
begin() {
    if (this.delegate.begin) {
      this.delegate.begin();
    }
  }
/**
 * @return {?}
 */
private _scheduleCountTask() {
    Zone.current.scheduleMicroTask('incremenet the animation microtask', () => this._microtaskId++);
  }
/**
 * @param {?} count
 * @param {?} fn
 * @param {?} data
 * @return {?}
 */
scheduleListenerCallback(count: number, fn: (e: any) => any, data: any) {
    if (count >= 0 && count < this._microtaskId) {
      this._zone.run(() => fn(data));
      return;
    }

    if (this._animationCallbacksBuffer.length == 0) {
      Promise.resolve(null).then(() => {
        this._zone.run(() => {
          this._animationCallbacksBuffer.forEach(tuple => {
            const [fn, data] = tuple;
            fn(data);
          });
          this._animationCallbacksBuffer = [];
        });
      });
    }

    this._animationCallbacksBuffer.push([fn, data]);
  }
/**
 * @return {?}
 */
end() {
    this._zone.runOutsideAngular(() => {
      this._scheduleCountTask();
      this.engine.flush(this._microtaskId);
    });
    if (this.delegate.end) {
      this.delegate.end();
    }
  }
/**
 * @return {?}
 */
whenRenderingDone(): Promise<any> { return this.engine.whenRenderingDone(); }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: RendererFactory2, },
{type: AnimationEngine, },
{type: NgZone, },
];
}

function AnimationRendererFactory_tsickle_Closure_declarations() {
/** @type {?} */
AnimationRendererFactory.decorators;
/**
 * @nocollapse
 * @type {?}
 */
AnimationRendererFactory.ctorParameters;
/** @type {?} */
AnimationRendererFactory.prototype._currentId;
/** @type {?} */
AnimationRendererFactory.prototype._microtaskId;
/** @type {?} */
AnimationRendererFactory.prototype._animationCallbacksBuffer;
/** @type {?} */
AnimationRendererFactory.prototype._rendererCache;
/** @type {?} */
AnimationRendererFactory.prototype.delegate;
/** @type {?} */
AnimationRendererFactory.prototype.engine;
/** @type {?} */
AnimationRendererFactory.prototype._zone;
}

export class BaseAnimationRenderer implements Renderer2 {
/**
 * @param {?} namespaceId
 * @param {?} delegate
 * @param {?} engine
 */
constructor(
      protected namespaceId: string,
public delegate: Renderer2,
public engine: AnimationEngine) {
    this.destroyNode = this.delegate.destroyNode ? (n) => delegate.destroyNode !(n) : null;
  }
/**
 * @return {?}
 */
get data() { return this.delegate.data; }

  destroyNode: ((n: any) => void)|null;
/**
 * @return {?}
 */
destroy(): void {
    this.engine.destroy(this.namespaceId, this.delegate);
    this.delegate.destroy();
  }
/**
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
createElement(name: string, namespace?: string|null|undefined) {
    return this.delegate.createElement(name, namespace);
  }
/**
 * @param {?} value
 * @return {?}
 */
createComment(value: string) { return this.delegate.createComment(value); }
/**
 * @param {?} value
 * @return {?}
 */
createText(value: string) { return this.delegate.createText(value); }
/**
 * @param {?} parent
 * @param {?} newChild
 * @return {?}
 */
appendChild(parent: any, newChild: any): void {
    this.delegate.appendChild(parent, newChild);
    this.engine.onInsert(this.namespaceId, newChild, parent, false);
  }
/**
 * @param {?} parent
 * @param {?} newChild
 * @param {?} refChild
 * @return {?}
 */
insertBefore(parent: any, newChild: any, refChild: any): void {
    this.delegate.insertBefore(parent, newChild, refChild);
    this.engine.onInsert(this.namespaceId, newChild, parent, true);
  }
/**
 * @param {?} parent
 * @param {?} oldChild
 * @return {?}
 */
removeChild(parent: any, oldChild: any): void {
    this.engine.onRemove(this.namespaceId, oldChild, this.delegate);
  }
/**
 * @param {?} selectorOrNode
 * @return {?}
 */
selectRootElement(selectorOrNode: any) { return this.delegate.selectRootElement(selectorOrNode); }
/**
 * @param {?} node
 * @return {?}
 */
parentNode(node: any) { return this.delegate.parentNode(node); }
/**
 * @param {?} node
 * @return {?}
 */
nextSibling(node: any) { return this.delegate.nextSibling(node); }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @param {?=} namespace
 * @return {?}
 */
setAttribute(el: any, name: string, value: string, namespace?: string|null|undefined): void {
    this.delegate.setAttribute(el, name, value, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?=} namespace
 * @return {?}
 */
removeAttribute(el: any, name: string, namespace?: string|null|undefined): void {
    this.delegate.removeAttribute(el, name, namespace);
  }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
addClass(el: any, name: string): void { this.delegate.addClass(el, name); }
/**
 * @param {?} el
 * @param {?} name
 * @return {?}
 */
removeClass(el: any, name: string): void { this.delegate.removeClass(el, name); }
/**
 * @param {?} el
 * @param {?} style
 * @param {?} value
 * @param {?=} flags
 * @return {?}
 */
setStyle(el: any, style: string, value: any, flags?: RendererStyleFlags2|undefined): void {
    this.delegate.setStyle(el, style, value, flags);
  }
/**
 * @param {?} el
 * @param {?} style
 * @param {?=} flags
 * @return {?}
 */
removeStyle(el: any, style: string, flags?: RendererStyleFlags2|undefined): void {
    this.delegate.removeStyle(el, style, flags);
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    this.delegate.setProperty(el, name, value);
  }
/**
 * @param {?} node
 * @param {?} value
 * @return {?}
 */
setValue(node: any, value: string): void { this.delegate.setValue(node, value); }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(target: any, eventName: string, callback: (event: any) => boolean | void): () => void {
    return this.delegate.listen(target, eventName, callback);
  }
}

function BaseAnimationRenderer_tsickle_Closure_declarations() {
/** @type {?} */
BaseAnimationRenderer.prototype.destroyNode;
/** @type {?} */
BaseAnimationRenderer.prototype.namespaceId;
/** @type {?} */
BaseAnimationRenderer.prototype.delegate;
/** @type {?} */
BaseAnimationRenderer.prototype.engine;
}

export class AnimationRenderer extends BaseAnimationRenderer implements Renderer2 {
/**
 * @param {?} factory
 * @param {?} namespaceId
 * @param {?} delegate
 * @param {?} engine
 */
constructor(
public factory: AnimationRendererFactory, namespaceId: string, delegate: Renderer2,
      engine: AnimationEngine) {
    super(namespaceId, delegate, engine);
    this.namespaceId = namespaceId;
  }
/**
 * @param {?} el
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setProperty(el: any, name: string, value: any): void {
    if (name.charAt(0) == '@') {
      name = name.substr(1);
      this.engine.setProperty(this.namespaceId, el, name, value);
    } else {
      this.delegate.setProperty(el, name, value);
    }
  }
/**
 * @param {?} target
 * @param {?} eventName
 * @param {?} callback
 * @return {?}
 */
listen(target: 'window'|'document'|'body'|any, eventName: string, callback: (event: any) => any):
      () => void {
    if (eventName.charAt(0) == '@') {
      const /** @type {?} */ element = resolveElementFromTarget(target);
      let /** @type {?} */ name = eventName.substr(1);
      let /** @type {?} */ phase = '';
      if (name.charAt(0) != '@') {  // transition-specific
        [name, phase] = parseTriggerCallbackName(name);
      }
      return this.engine.listen(this.namespaceId, element, name, phase, event => {
        const /** @type {?} */ countId = ( /** @type {?} */((event as any)))['_data'] || -1;
        this.factory.scheduleListenerCallback(countId, callback, event);
      });
    }
    return this.delegate.listen(target, eventName, callback);
  }
}

function AnimationRenderer_tsickle_Closure_declarations() {
/** @type {?} */
AnimationRenderer.prototype.factory;
}

/**
 * @param {?} target
 * @return {?}
 */
function resolveElementFromTarget(target: 'window' | 'document' | 'body' | any): any {
  switch (target) {
    case 'body':
      return document.body;
    case 'document':
      return document;
    case 'window':
      return window;
    default:
      return target;
  }
}
/**
 * @param {?} triggerName
 * @return {?}
 */
function parseTriggerCallbackName(triggerName: string) {
  const /** @type {?} */ dotIndex = triggerName.indexOf('.');
  const /** @type {?} */ trigger = triggerName.substring(0, dotIndex);
  const /** @type {?} */ phase = triggerName.substr(dotIndex + 1);
  return [trigger, phase];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
